import {
  Alert,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useUpdateVerificationsMutation } from "Api/Mutations/Verifications/useUpdateVerificationsMutation";
import { useCreatePartyRelationMutation } from "Api/Mutations/Party/useCreatePartyRelationMutation";
import { OwnerCard } from "Components/Onboarding/BusinessEntity/Steps/BeneficialOwnersStep/OwnerCard";
import { OwnerModal } from "Components/Onboarding/BusinessEntity/Steps/BeneficialOwnersStep/OwnerModal/OwnerModal";
import { StepBox } from "Components/Onboarding/shared/StepBox";
import { BlTextButton } from "Components/Shared/Buttons/BlTextButton";
import { FullHeightForm } from "Components/Shared/FormStyles";
import { ValuesBox } from "Components/Shared/ValuesBox/ValuesBox";
import {
  RelationType,
  type UserVerificationDto,
  type UserVerificationUpdateDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { useState } from "react";
import { toast } from "sonner";

const NEW_OWNER_ID = "";
const EMPTY_OWNER_ID = null;
const StepResources =
  Resources.Onboarding.BusinessOnboarding.Steps.BeneficialOwners;

type PrimaryChoice = "iAmOwner" | "notTheOnlyOwner";
type SecondaryChoice = "manualEntry" | "unknown";

type Props = {
  onNextStepRequested: () => void;
  isActingPartyOwner: boolean | null;
  ownerPartiesPublicIDs: Array<string>;
  verificationData: UserVerificationDto;
  userPartyPublicID: string | undefined;
  actingPartyPublicID: string | undefined;
};

const getInitialChoices = (
  isActingPartyOwner: boolean | null,
  ownerPartiesPublicIDs: Array<string>,
  uboUnknown: boolean | undefined,
): { primary: PrimaryChoice | null; secondary: SecondaryChoice | null } => {
  if (uboUnknown) {
    return { primary: "notTheOnlyOwner", secondary: "unknown" };
  }
  if (isActingPartyOwner && ownerPartiesPublicIDs.length === 0) {
    return { primary: "iAmOwner", secondary: null };
  }
  if (ownerPartiesPublicIDs.length > 0) {
    return { primary: "notTheOnlyOwner", secondary: "manualEntry" };
  }
  return { primary: null, secondary: null };
};

const toUpdateDto = (
  verificationData: UserVerificationDto,
  overrides: Partial<UserVerificationUpdateDto>,
): UserVerificationUpdateDto => ({
  businessActivity: verificationData.businessActivity,
  otherBusinessActivityDetails: verificationData.otherBusinessActivityDetails,
  sourcesOfIncome: verificationData.sourcesOfIncome,
  otherSourceOfIncomeDetails: verificationData.otherSourceOfIncomeDetails,
  expectedMonthlyRevenue: verificationData.expectedMonthlyRevenue,
  averageTransactionValue: verificationData.averageTransactionValue,
  taxResidency: verificationData.taxResidency,
  isPoliticallyExposedPerson: verificationData.isPoliticallyExposedPerson,
  hasNonEUNationality: verificationData.hasNonEUNationality,
  hasNonEUResidence: verificationData.hasNonEUResidence,
  uboUnknown: verificationData.uboUnknown,
  ...overrides,
});

export const BeneficialOwnersStepContent = ({
  onNextStepRequested,
  isActingPartyOwner,
  ownerPartiesPublicIDs,
  verificationData,
  userPartyPublicID,
  actingPartyPublicID,
}: Props) => {
  const [modalOwnerID, setModalOwnerID] = useState<string | null>(
    EMPTY_OWNER_ID,
  );
  const [{ primary: initialPrimary, secondary: initialSecondary }] = useState(
    () =>
      getInitialChoices(
        isActingPartyOwner,
        ownerPartiesPublicIDs,
        verificationData.uboUnknown,
      ),
  );
  const [primaryChoice, setPrimaryChoice] = useState<PrimaryChoice | null>(
    initialPrimary,
  );
  const [secondaryChoice, setSecondaryChoice] =
    useState<SecondaryChoice | null>(initialSecondary);

  const { t } = useResource();
  const { mutateAsync: postPartyRelation, isPending: isPartyRelationCreating } =
    useCreatePartyRelationMutation();
  const {
    mutateAsync: updateVerifications,
    isPending: isVerificationUpdating,
  } = useUpdateVerificationsMutation({});

  const isSubmitDisabled =
    !primaryChoice ||
    (primaryChoice === "notTheOnlyOwner" && !secondaryChoice) ||
    (secondaryChoice === "manualEntry" && !ownerPartiesPublicIDs.length);

  const handleSubmit = async () => {
    if (!primaryChoice) {
      return;
    }

    try {
      if (primaryChoice === "iAmOwner") {
        const shouldAddActingPartyAsOwner =
          userPartyPublicID && actingPartyPublicID && !isActingPartyOwner;

        if (shouldAddActingPartyAsOwner) {
          await postPartyRelation({
            partyPublicID: userPartyPublicID,
            data: {
              relatedPartyPublicID: actingPartyPublicID,
              relationType: RelationType.BeneficialOwner,
            },
          });
        }

        if (verificationData.uboUnknown) {
          await updateVerifications(
            toUpdateDto(verificationData, { uboUnknown: false }),
          );
        }
      } else if (secondaryChoice === "unknown") {
        if (!verificationData.uboUnknown) {
          await updateVerifications(
            toUpdateDto(verificationData, { uboUnknown: true }),
          );
        }
      } else if (secondaryChoice === "manualEntry") {
        if (verificationData.uboUnknown) {
          await updateVerifications(
            toUpdateDto(verificationData, { uboUnknown: false }),
          );
        }
      }
    } catch (_error) {
      toast.error(t(StepResources.SaveError));
      return;
    }

    onNextStepRequested();
  };

  const handlePrimaryChange = (_: React.ChangeEvent, val: string) => {
    const value = val as PrimaryChoice;
    setPrimaryChoice(value);
    if (value === "iAmOwner") {
      setSecondaryChoice(null);
    }
  };

  return (
    <>
      <FullHeightForm
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <StepBox
          submitButtonLabel={t(Resources.Common.Continue)}
          isSubmitting={isPartyRelationCreating || isVerificationUpdating}
          submitDisabled={isSubmitDisabled}
        >
          <Typography variant="body2" mb={2}>
            {t(StepResources.Description)}
          </Typography>
          <Stack gap={2}>
            <FormControl>
              <FormLabel sx={{ "&.Mui-focused": { color: "text.primary" } }}>
                {t(StepResources.Question)}
              </FormLabel>
              <RadioGroup
                value={primaryChoice ?? ""}
                onChange={handlePrimaryChange}
              >
                <FormControlLabel
                  value="iAmOwner"
                  control={<Radio />}
                  label={t(StepResources.UboChoice.IAmOwner)}
                />
                <FormControlLabel
                  value="notTheOnlyOwner"
                  control={<Radio />}
                  label={t(StepResources.UboChoice.NotTheOnlyOwner)}
                />
              </RadioGroup>
            </FormControl>

            {primaryChoice === "iAmOwner" && (
              <Alert severity="success">
                {t(StepResources.UboChoice.IAmOwnerDescription)}
              </Alert>
            )}

            {primaryChoice === "notTheOnlyOwner" && (
              <FormControl>
                <FormLabel sx={{ "&.Mui-focused": { color: "text.primary" } }}>
                  {t(StepResources.SecondaryQuestion)}
                </FormLabel>
                <RadioGroup
                  value={secondaryChoice ?? ""}
                  onChange={(_, val) =>
                    setSecondaryChoice(val as SecondaryChoice)
                  }
                >
                  <FormControlLabel
                    value="manualEntry"
                    control={<Radio />}
                    label={t(StepResources.UboChoice.ManualEntry)}
                  />
                  <FormControlLabel
                    value="unknown"
                    control={<Radio />}
                    label={t(StepResources.UboChoice.Unknown)}
                  />
                </RadioGroup>
              </FormControl>
            )}

            {secondaryChoice === "manualEntry" &&
              primaryChoice === "notTheOnlyOwner" && (
                <ValuesBox title={t(StepResources.OwnersTitle)}>
                  {ownerPartiesPublicIDs.map((id) => (
                    <OwnerCard
                      key={id}
                      publicID={id}
                      onEdit={() => setModalOwnerID(id)}
                    />
                  ))}
                  <div>
                    <BlTextButton
                      color="primary"
                      onClick={() => setModalOwnerID(NEW_OWNER_ID)}
                    >
                      {`+ ${t(
                        ownerPartiesPublicIDs.length
                          ? StepResources.AddNextOwner
                          : StepResources.AddOwner,
                      )}`}
                    </BlTextButton>
                  </div>
                </ValuesBox>
              )}

            {secondaryChoice === "unknown" &&
              primaryChoice === "notTheOnlyOwner" && (
                <Alert severity="info">
                  {t(StepResources.UboChoice.UnknownDescription)}
                </Alert>
              )}
          </Stack>
        </StepBox>
      </FullHeightForm>
      <OwnerModal
        publicID={modalOwnerID || undefined}
        isOpen={modalOwnerID !== EMPTY_OWNER_ID}
        onClose={() => setModalOwnerID(EMPTY_OWNER_ID)}
      />
    </>
  );
};
